import './Results.scss';

import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { faCircleArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FamilyProfileSwitcher } from '@components/FamilyProfileSwitcher/FamilyProfileSwitcher';
import { NeedHelp } from '@components/NeedHelp/NeedHelp';
import { RerequisitionCallout } from '@components/RerequisitionCallout/RerequisitionCallout';
import { Sanitize } from '@components/Sanitize';
import { ROUTES } from '@constants/routes';
import { Order, OrderStatus } from '@customTypes/order';
import { GeneticCounselingRequest, Requisition } from '@customTypes/requisition';
import { getRequisitionStatusKey } from '@helpers/requisition';
import useApp from '@hooks/useApp';
import { useSelectedProfile } from '@hooks/useSelectedProfile';
import ContentContainer from '@layouts/container/ContentContainer';
import PageContainer from '@layouts/container/PageContainer';
import { fetchGeneticCounselingDetails } from '@store/actions';
import { downloadAllResults, fetchOpenRequisitions, fetchRerequisitionEligibility } from '@store/actions/patientPortal';
import { recordGoogleAnalyticsSend } from '@utils/helpers';

import RequestGeneticCounseling from './components/RequestGeneticCounseling/RequestGeneticCounseling';
import { ResultCard } from './components/ResultCard/ResultCard';
import ShareResultsCard from './components/ShareResultsCard/ShareResultsCard';
import { getUniqueResults } from './Results.utils';

const SELF_PROFILE_KEY = 'self';

// A guardian whose access has lapsed gets a 404 here, which should read as "no
// counseling to show" rather than surfacing an error to them.
const loadCounseling = (individualId: string) =>
  fetchGeneticCounselingDetails(individualId, { suppressErrorToast: true }).catch(error => {
    console.error('Error fetching genetic counseling details:', error);
    return null;
  });

// A minor with multiple requisitions can otherwise mismatch the counseling card's
// status and scheduling link, so prefer the requisition the counseling record points at.
const selectViewedRequisition = (
  requisitions: Requisition[],
  counseling: GeneticCounselingRequest | null,
): Requisition | null => {
  if (!requisitions.length) return null;
  if (counseling?.requisition_id) {
    const matched = requisitions.find(req => req.requisition_id === counseling.requisition_id);
    if (matched) return matched;
  }
  const mostRecentCompleted = [...requisitions]
    .reverse()
    .find(req => getRequisitionStatusKey(req) === OrderStatus.COMPLETED);
  return mostRecentCompleted ?? requisitions[requisitions.length - 1];
};

const Results = () => {
  const { familyProfiles, mostRecentTestRequest, userFirstName } = useApp();
  const [results, setResults] = useState<Order[]>([]);
  const [hasOrders, setHasOrders] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isRerequisitionEligible, setIsRerequisitionEligible] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { activeProfile, activeProfileKey, isViewingSelf, profiles, selectProfile, selectedIndividual } =
    useSelectedProfile(familyProfiles);
  // A minor's requisition and counseling record, which the store never holds.
  const [viewedRequisition, setViewedRequisition] = useState<Requisition | null>(null);
  const [viewedCounseling, setViewedCounseling] = useState<GeneticCounselingRequest | null>(null);
  const requisitionsByProfile = useRef(new Map<string, Requisition[]>());
  const rerequisitionEligibilityByProfile = useRef(new Map<string, boolean>());
  const requestSequence = useRef(0);
  // Set when a click already started this profile, so the effect does not load it again.
  const loadedProfileKey = useRef<string | null>(null);

  const profileKey = (individualIdentifier?: string) => individualIdentifier ?? SELF_PROFILE_KEY;

  const loadProfileResults = async (individualIdentifier?: string) => {
    const key = profileKey(individualIdentifier);
    loadedProfileKey.current = key;
    const requestId = ++requestSequence.current;
    const isStale = () => requestId !== requestSequence.current;

    // The logged-in user's counseling record is already in the store from app start,
    // so only a minor's has to be fetched. Runs alongside the requisitions rather
    // than delaying the results list.
    const counselingRequest = individualIdentifier ? loadCounseling(individualIdentifier) : Promise.resolve(null);

    let requisitions: Requisition[] = [];
    if (requisitionsByProfile.current.has(key)) {
      requisitions = requisitionsByProfile.current.get(key)!;
      setResults(getUniqueResults(requisitions));
      setHasOrders(requisitions.length > 0);
      setIsRerequisitionEligible(rerequisitionEligibilityByProfile.current.get(key) === true);
      setLoading(false);
      setLoadError(false);
    } else {
      setLoading(true);
      setLoadError(false);
      setIsRerequisitionEligible(false);
      // Clear with loading so the empty-state copy does not flash between profiles.
      setResults([]);
      setHasOrders(false);
      try {
        const eligibilityRequest = fetchRerequisitionEligibility(individualIdentifier)
          .then(response => response.is_eligible === true)
          .catch(() => false);

        const [response, isEligible] = await Promise.all([
          fetchOpenRequisitions(individualIdentifier),
          eligibilityRequest,
        ]);
        if (isStale()) return;
        requisitions = response.requisitions;
        requisitionsByProfile.current.set(key, requisitions);
        rerequisitionEligibilityByProfile.current.set(key, isEligible);
        setIsRerequisitionEligible(isEligible);
        setResults(getUniqueResults(requisitions));
        setHasOrders(requisitions.length > 0);
      } catch (error) {
        if (isStale()) return;
        console.error('Error fetching open requisitions:', error);
        setLoadError(true);
        setIsRerequisitionEligible(false);
        setResults([]);
        setHasOrders(false);
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    }

    // The counseling card reads whole requisitions (orders and their statuses),
    // not the flattened orders the results list uses.
    const counseling = await counselingRequest;
    if (isStale()) return;
    setViewedCounseling(counseling);
    setViewedRequisition(individualIdentifier ? selectViewedRequisition(requisitions, counseling) : null);
  };

  const onDownloadAll = async () => {
    try {
      setIsDownloading(true);
      await downloadAllResults(selectedIndividual);
    } catch (err) {
      console.error('Error downloading all documents:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const onSelectProfile = (identifier: string) => {
    const selection = selectProfile(identifier);
    if (!selection.changed) return;

    setIsDownloading(false);
    // A previous profile's counseling must not leak onto the newly selected one.
    setViewedRequisition(null);
    setViewedCounseling(null);
    // Same turn as the URL update, so the previous person's cards do not paint under the new tab.
    loadProfileResults(selection.individualId);
  };

  useEffect(() => {
    recordGoogleAnalyticsSend('pageview', ROUTES.RESULTS, 'Landing on Results Page');
    // Pre-split results pageview, so reports that count /home as viewed results still match.
    recordGoogleAnalyticsSend('pageview', ROUTES.HOME, 'Landing on Home Page');
  }, []);

  useEffect(() => {
    if (loadedProfileKey.current === profileKey(selectedIndividual)) return;
    loadProfileResults(selectedIndividual);
    // The loader closes over refs. Depending on it would refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndividual]);

  const showEmptyState = !loading && !loadError && !results.length;
  const emptyResultsHeading =
    !isViewingSelf && activeProfile?.first_name ? (
      <>
        <Sanitize>{activeProfile.first_name}</Sanitize> has no results to view at this time.
      </>
    ) : (
      'You have no results to view at this time.'
    );

  return (
    <PageContainer>
      <ContentContainer containerClass='bg-body py-4 results-header'>
        <h1 className='text-primary'>
          {' '}
          Welcome, <Sanitize>{userFirstName}</Sanitize>
        </h1>
        <FamilyProfileSwitcher profiles={profiles} activeKey={activeProfileKey} onSelect={onSelectProfile} />
        {isRerequisitionEligible && <RerequisitionCallout individualId={selectedIndividual} />}
        {/* Guardians can schedule counseling for a minor as well as for themselves. */}
        <RequestGeneticCounseling
          requisitionId={isViewingSelf ? mostRecentTestRequest?.requisition_id : viewedRequisition?.requisition_id}
          viewing={selectedIndividual}
          viewedRequisition={viewedRequisition}
          viewedCounseling={viewedCounseling}
          viewedFirstName={activeProfile?.first_name}
        />
      </ContentContainer>
      <ContentContainer
        containerClass='page-content results-content'
        contentClass='d-flex flex-column flex-lg-row gap-4'
      >
        <div className='d-flex flex-column gap-4 results-content-left'>
          <div className='d-flex flex-column gap-3'>
            <h4 className='text-primary pb-2 border-bottom fw-semibold mb-0'>Results</h4>
            {loadError && !loading && (
              <Alert variant='danger' className='mb-0'>
                <Alert.Heading as='h5'>Error loading results</Alert.Heading>
                <p className='mb-0'>
                  We encountered an error while loading results. Please reload the page. If the problem persists, please
                  contact us at <a href='mailto:support@myome.com'>support@myome.com</a>.
                </p>
              </Alert>
            )}
            {showEmptyState && !hasOrders && (
              <p className='mb-0'>
                There aren&apos;t any results for this individual yet. Once an order is placed, you&apos;ll be able to
                track it on the homepage and download results here.
              </p>
            )}
            {showEmptyState && hasOrders && (
              <>
                <h5 className='mb-0'>{emptyResultsHeading}</h5>
                <p>
                  Your order is in progress. Order status can be viewed in the{' '}
                  <span className='fw-semibold'>My Orders</span> section of your portal.
                </p>
                <p>We will send you an email once your results are ready for review.</p>
              </>
            )}
            {results.length > 0 && (
              <>
                <p className='mb-1'>
                  Important information about your results is listed below. Click <strong>View details</strong> to learn
                  more about each result.
                </p>
                <p>
                  Use the <strong>Download all results</strong> option below to download a copy of your results. The
                  download will contain all results and your clinical summary letter consolidated into a single PDF.
                </p>
                <Button
                  variant='primary'
                  className='d-inline-flex align-items-center gap-2 align-self-start'
                  onClick={onDownloadAll}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
                  ) : (
                    <FontAwesomeIcon icon={faCircleArrowDown} />
                  )}
                  Download all results
                </Button>
              </>
            )}
          </div>
          {results.length > 0 && (
            <ul className='results-list'>
              {results.map(result => (
                <ResultCard key={result.order_id} result={result} individual={selectedIndividual} />
              ))}
            </ul>
          )}
          {loading && (
            <div className='text-center mt-5 text-primary'>
              <Spinner animation='border' />
            </div>
          )}
        </div>

        <div className='d-flex flex-column gap-4 results-content-right'>
          <NeedHelp />
          {/* Sharing is scoped to the caller's own results, so hide it when viewing a minor. */}
          {results.length > 0 && isViewingSelf && <ShareResultsCard />}
        </div>
      </ContentContainer>
    </PageContainer>
  );
};

export default Results;
